(function () {
  "use strict";

  const BERLIN_TIMEZONE = "Europe/Berlin";

  function getBerlinNowParts() {
    const formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone: BERLIN_TIMEZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23"
    });

    const parts = formatter.formatToParts(new Date());
    const map = {};

    parts.forEach((part) => {
      if (part.type !== "literal") {
        map[part.type] = part.value;
      }
    });

    return {
      year: parseInt(map.year, 10),
      month: parseInt(map.month, 10),
      day: parseInt(map.day, 10),
      hour: parseInt(map.hour, 10),
      minute: parseInt(map.minute, 10),
      second: parseInt(map.second, 10)
    };
  }

  function makeUtcDateFromBerlinParts(parts) {
    return new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12, 0, 0));
  }

  function cloneUtcDate(date) {
    return new Date(date.getTime());
  }

  function addUtcDays(date, days) {
    const next = cloneUtcDate(date);
    next.setUTCDate(next.getUTCDate() + days);
    return next;
  }

  function getUtcWeekday(date) {
    return date.getUTCDay(); // 0=So, 1=Mo, ..., 6=Sa
  }

  function isShippingDay(date) {
    const day = getUtcWeekday(date);
    return day >= 1 && day <= 5; // Mo-Fr
  }

  function isDeliveryDay(date) {
    const day = getUtcWeekday(date);
    return day !== 0; // Mo-Sa
  }

  function getShipDate() {
    const nowBerlin = getBerlinNowParts();
    let shipDate = makeUtcDateFromBerlinParts(nowBerlin);

    const weekday = getUtcWeekday(shipDate);
    const beforeCutoff =
      nowBerlin.hour < 14 || (nowBerlin.hour === 14 && nowBerlin.minute === 0 && nowBerlin.second === 0);

    if (weekday >= 1 && weekday <= 5 && beforeCutoff) {
      return shipDate;
    }

    do {
      shipDate = addUtcDays(shipDate, 1);
    } while (!isShippingDay(shipDate));

    return shipDate;
  }

  function addDeliveryWorkingDays(startDate, count) {
    let date = cloneUtcDate(startDate);
    let added = 0;

    while (added < count) {
      date = addUtcDays(date, 1);
      if (isDeliveryDay(date)) {
        added += 1;
      }
    }

    return date;
  }

  function getTransitRange(countryCode) {
    const code = String(countryCode || "DE").toUpperCase();

    if (code === "DE") {
      return { min: 1, max: 2 };
    }

    if (code === "AT") {
      return { min: 2, max: 3 };
    }

    return { min: 2, max: 4 };
  }

  function normalizeLocale(locale) {
    const value = String(locale || document.documentElement.lang || "de").toLowerCase();

    const map = {
      de: "de-DE",
      en: "en-GB",
      es: "es-ES",
      fr: "fr-FR",
      it: "it-IT",
      nl: "nl-NL"
    };

    if (map[value]) return map[value];

    const short = value.split("-")[0];
    return map[short] || "de-DE";
  }

  function formatLocalizedDate(date, locale) {
    return new Intl.DateTimeFormat(locale, {
      timeZone: "UTC",
      weekday: "short",
      day: "2-digit",
      month: "short"
    }).format(date);
  }

  function getEstimateMarkup(root) {
    const locale = normalizeLocale(root.dataset.locale);
    const shipDate = getShipDate();
    const range = getTransitRange(root.dataset.countryCode);

    const earliest = addDeliveryWorkingDays(shipDate, range.min);
    const latest = addDeliveryWorkingDays(shipDate, range.max);

    const availableText = root.dataset.textAvailable || "Sofort lieferbar";
    const deliveryText = root.dataset.textDelivery || "Voraussichtliche Lieferung:";
    const rangeSeparator = root.dataset.textRangeSeparator || "–";

    const earliestText = formatLocalizedDate(earliest, locale);
    const latestText = formatLocalizedDate(latest, locale);

    let deliveryMarkup = "";

    if (earliestText === latestText) {
      deliveryMarkup = `<strong>${earliestText}</strong>`;
    } else {
      deliveryMarkup = `<strong>${earliestText} ${rangeSeparator} ${latestText}</strong>`;
    }

    return `${availableText} – ${deliveryText} ${deliveryMarkup}`;
  }

  function updateEstimate(root, variant) {
    if (!root) return;

    const textNode = root.querySelector("[data-shipping-estimate-text]");
    if (!textNode) return;

    let available = root.dataset.available === "true";

    if (variant && typeof variant.available !== "undefined") {
      available = !!variant.available;
    }

    if (!available) {
      root.hidden = true;
      return;
    }

    root.hidden = false;
    textNode.innerHTML = getEstimateMarkup(root);
  }

  function initEstimate(root) {
    if (!root || root.dataset.shippingEstimateInitialized === "true") return;
    root.dataset.shippingEstimateInitialized = "true";
    updateEstimate(root, null);
  }

  function initAll(scope) {
    (scope || document)
      .querySelectorAll("[data-shipping-estimate]")
      .forEach(initEstimate);
  }

  function getProductScope(node) {
    if (!node || typeof node.closest !== "function") return document;

    return (
      node.closest(".product") ||
      node.closest(".quick-product") ||
      node.closest("[data-section-type='product']") ||
      document
    );
  }

  function rootMatchesVariant(root, variantId) {
    if (!variantId) return true;

    var scope = getProductScope(root);
    if (!scope || typeof scope.querySelector !== "function") return true;

    var variantInput = scope.querySelector("[data-product-form] [name='id'], [data-product-form] [data-variant-select]");
    if (!variantInput) return true;

    return String(variantInput.value || "") === String(variantId);
  }

  document.addEventListener("DOMContentLoaded", function () {
    initAll(document);
  });

  document.addEventListener("product:variant-change", function (evt) {
    const variant = evt.detail && evt.detail.variant;
    const variantId = variant && variant.id ? String(variant.id) : "";

    document.querySelectorAll("[data-shipping-estimate]").forEach(function (root) {
      if (!rootMatchesVariant(root, variantId)) return;
      updateEstimate(root, variant);
    });
  });

  document.addEventListener("quickview:loaded", function () {
    initAll(document);
  });

  document.addEventListener("shopify:section:load", function (evt) {
    if (evt.target) initAll(evt.target);
  });
})();